import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const selectClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white';

export default function PromotionTab() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceYearId, setSourceYearId] = useState('');
  const [targetYearId, setTargetYearId] = useState('');
  const [mapping, setMapping] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/admin/promotions/school-years')
      .then(res => {
        setYears(res.data);
        const active = res.data.find(y => y.isActive);
        if (active) {
          // Par défaut, source = active, cible = vide
          setSourceYearId(active.id);
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Erreur de chargement des années'))
      .finally(() => setLoading(false));
  }, []);

  const sourceYear = useMemo(() => years.find(y => y.id === sourceYearId), [years, sourceYearId]);
  const targetYear = useMemo(() => years.find(y => y.id === targetYearId), [years, targetYearId]);

  // Initialise le mapping quand source/target changent
  useEffect(() => {
    setPreview(null);
    setResult(null);
    setError('');
    if (!sourceYear) {
      setMapping([]);
      return;
    }

    const existingTargets = targetYear ? targetYear.classes.map(c => c.name) : [];
    const initialMapping = sourceYear.classes.map(c => {
      // Heuristique simple : BTS1 -> BTS2, Terminale -> sortie, etc.
      const suggestedName = suggestTargetName(c.name);
      const existingTarget = existingTargets.includes(suggestedName)
        ? targetYear.classes.find(tc => tc.name === suggestedName)
        : null;

      return {
        sourceClasseId: c.id,
        sourceClasseName: c.name,
        targetClasseId: existingTarget ? existingTarget.id : '',
        targetClasseName: suggestedName,
        mode: existingTarget ? 'existing' : 'create',
      };
    });
    setMapping(initialMapping);
  }, [sourceYear, targetYear]);

  function suggestTargetName(name) {
    const upper = name.toUpperCase();
    if (upper.includes('BTS1')) return name.replace(/BTS1/i, 'BTS2');
    if (upper.includes('1ERE') || upper.includes('1ÈRE')) return name.replace(/1[EÈ]RE/i, 'TERMINALE');
    if (upper.includes('2NDE')) return name.replace(/2NDE/i, '1ERE');
    return '';
  }

  const updateMapping = (index, updates) => {
    setMapping(prev => prev.map((m, i) => (i === index ? { ...m, ...updates } : m)));
    setPreview(null);
    setResult(null);
  };

  const handlePreview = async (e) => {
    e.preventDefault();
    setError('');
    setPreview(null);
    setResult(null);

    const activeMapping = mapping
      .filter(m => m.targetClasseId || m.targetClasseName?.trim())
      .map(m => ({
        sourceClasseId: m.sourceClasseId,
        targetClasseId: m.targetClasseId || undefined,
        targetClasseName: m.targetClasseName?.trim() || undefined,
      }));

    if (activeMapping.length === 0) {
      setError('Aucun mapping actif. Définissez au moins une classe cible.');
      return;
    }

    setPreviewLoading(true);
    try {
      const res = await api.post('/admin/promotions/preview', {
        sourceYearId,
        targetYearId,
        mapping: activeMapping,
      });
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!window.confirm('Confirmez-vous l\'exécution de cette promotion ? Cette action est définitive.')) return;

    setError('');
    setExecuteLoading(true);
    try {
      const activeMapping = mapping
        .filter(m => m.targetClasseId || m.targetClasseName?.trim())
        .map(m => ({
          sourceClasseId: m.sourceClasseId,
          targetClasseId: m.targetClasseId || undefined,
          targetClasseName: m.targetClasseName?.trim() || undefined,
        }));

      const res = await api.post('/admin/promotions/execute', {
        sourceYearId,
        targetYearId,
        mapping: activeMapping,
      });
      setResult(res.data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'exécution');
    } finally {
      setExecuteLoading(false);
    }
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Chargement...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h3 className="font-medium text-gray-800 mb-1">Promotion d'année scolaire</h3>
        <p className="text-sm text-gray-500">
          Faites passer les élèves d'une année scolaire à l'année suivante. Les affectations existantes sont conservées.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm space-y-1">
          <p className="font-medium">Promotion exécutée avec succès</p>
          <p>{result.totalPromoted} élève(s) promu(s) dans l'année cible.</p>
          <p className="text-xs text-green-600">ID de traçabilité : {result.promotionRunId}</p>
        </div>
      )}

      <form onSubmit={handlePreview} className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Année source</label>
            <select value={sourceYearId} onChange={e => setSourceYearId(e.target.value)} className={selectClass} required>
              <option value="">Choisir</option>
              {years.map(y => (
                <option key={y.id} value={y.id}>{y.label}{y.isActive ? ' (active)' : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Année cible</label>
            <select value={targetYearId} onChange={e => setTargetYearId(e.target.value)} className={selectClass} required>
              <option value="">Choisir</option>
              {years.filter(y => y.id !== sourceYearId).map(y => (
                <option key={y.id} value={y.id}>{y.label}{y.isActive ? ' (active)' : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {sourceYear && targetYear && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-800">Mapping des classes</h4>
            {mapping.length === 0 ? (
              <p className="text-sm text-gray-500">Aucune classe dans l'année source.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Classe source</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">→ Classe cible</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Mode</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {mapping.map((m, i) => (
                      <tr key={m.sourceClasseId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-800">{m.sourceClasseName}</span>
                          <span className="text-xs text-gray-400 ml-2">({sourceYear.classes.find(c => c.id === m.sourceClasseId)?.studentCount || 0} élèves)</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-2">
                            <select
                              value={m.mode}
                              onChange={e => {
                                const mode = e.target.value;
                                updateMapping(i, {
                                  mode,
                                  targetClasseId: mode === 'existing' ? (targetYear.classes[0]?.id || '') : '',
                                  targetClasseName: mode === 'create' ? suggestTargetName(m.sourceClasseName) : '',
                                });
                              }}
                              className="text-xs border border-gray-300 rounded-lg px-2 py-1 w-40"
                            >
                              <option value="existing">Classe existante</option>
                              <option value="create">Créer une classe</option>
                              <option value="skip">Ignorer</option>
                            </select>
                            {m.mode === 'existing' && (
                              <select
                                value={m.targetClasseId}
                                onChange={e => updateMapping(i, { targetClasseId: e.target.value })}
                                className={selectClass}
                              >
                                <option value="">Choisir une classe</option>
                                {targetYear.classes.map(c => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                            )}
                            {m.mode === 'create' && (
                              <input
                                type="text"
                                value={m.targetClasseName}
                                onChange={e => updateMapping(i, { targetClasseName: e.target.value })}
                                placeholder="ex: BTS2 NDRC"
                                className={inputClass}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {m.mode === 'skip' && <span className="text-xs text-gray-400">Ignoré</span>}
                          {m.mode === 'existing' && m.targetClasseId && (
                            <span className="text-xs text-blue-600">Affectation</span>
                          )}
                          {m.mode === 'create' && m.targetClasseName?.trim() && (
                            <span className="text-xs text-green-600">Création + affectation</span>
                          )}
                          {m.mode === 'existing' && !m.targetClasseId && (
                            <span className="text-xs text-red-500">À sélectionner</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={previewLoading || executeLoading || !sourceYearId || !targetYearId}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            {previewLoading ? 'Prévisualisation...' : 'Prévisualiser'}
          </button>
        </div>
      </form>

      {preview && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
          <h4 className="font-medium text-gray-800">Prévisualisation</h4>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{preview.summary.totalStudents}</p>
              <p className="text-sm text-blue-600">Élèves concernés</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{preview.summary.newAssignments}</p>
              <p className="text-sm text-green-600">Nouvelles affectations</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-yellow-700">{preview.summary.existingAssignments}</p>
              <p className="text-sm text-yellow-600">Déjà affectés</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{preview.summary.classesToCreate}</p>
              <p className="text-sm text-purple-600">Classes à créer</p>
            </div>
          </div>

          <div className="space-y-3">
            {preview.preview.map(item => (
              <div key={item.sourceClasse.id} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-800">
                    {item.sourceClasse.name} → {item.targetClasse?.name || 'Non défini'}
                  </p>
                  <span className="text-xs text-gray-500">
                    {item.studentCount} élève(s) — {item.newAssignments} nouveaux, {item.existingAssignments} déjà présents
                  </span>
                </div>
                {item.willCreateTargetClasse && (
                  <p className="text-xs text-green-600 mb-2">La classe cible sera créée.</p>
                )}
                {item.students.length > 0 && (
                  <div className="max-h-32 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody className="divide-y divide-gray-50">
                        {item.students.map(s => (
                          <tr key={s.id} className={s.alreadyAssigned ? 'text-gray-400' : 'text-gray-700'}>
                            <td className="py-1">{s.firstName} {s.lastName}</td>
                            <td className="py-1 text-gray-400">{s.ine || s.email || ''}</td>
                            <td className="py-1 text-right">
                              {s.alreadyAssigned && <span className="text-yellow-600">déjà affecté</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleExecute}
              disabled={executeLoading || preview.summary.newAssignments === 0}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
            >
              {executeLoading ? 'Exécution...' : 'Exécuter la promotion'}
            </button>
            <p className="text-xs text-gray-500">
              Cette action créera les classes manquantes et affectera les élèves à leurs classes cibles.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
